import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from '@/app/query-client';
import { router } from '@/app/router';
import { useAuth } from '@/lib/auth/auth-store';
import { LoginPage } from '@/features/auth/login-page';
import '@/styles/index.css';

/**
 * Auth gate at the root rather than in a route guard.
 *
 * The session restore is async (it exchanges a refresh token), so a per-route
 * redirect would race it and bounce a signed-in admin to the login screen on
 * every cold load. Holding the whole tree until the status is known is simpler
 * and correct.
 */
function Root() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);

  useEffect(() => {
    void restore();
  }, [restore]);

  if (status === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (status === 'anonymous') return <LoginPage />;

  return <RouterProvider router={router} />;
}

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
  </StrictMode>,
);
